import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import Loader from "../../../Template/Loader/Loader";
import { createGalleryPlanAPI } from "../../../utils/APIs/galleryPlansApis";
import { createPhotographyPlanAPI } from "../../../utils/APIs/photographyPlansApis";
import { createEditingPlanAPI } from "../../../utils/APIs/editingPlansApis";
import { toast } from "react-toastify";

const AddGalleryPlanModal = ({ show, onClose, refreshData, activeTab, subTab }) => {
  const [formData, setFormData] = useState({
    // Gallery fields
    days: "",
    charges: "",
    // Hourly fields
    role: "",
    pricingType: "duration_based",
    pricingOptions: [{ durationText: "", durationValue: "", price: "", subtitle: "" }],
    fixedPrice: "",
    features: [""],
    // Editing fields
    numberOfVideos: "",
    price: "",
    subtitle: "",
    planCategory: "standard",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if ((name === "days" || name === "charges" || name === "fixedPrice" || name === "price") && value < 0) return;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const preventInvalidChars = (e) => {
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleFeatureChange = (index, value) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures[index] = value;
    setFormData({ ...formData, features: updatedFeatures });
  };

  const addFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, ""],
    });
  };

  const removeFeature = (index) => {
    const updatedFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: updatedFeatures });
  };

  const handlePricingOptionChange = (index, field, value) => {
    const updatedOptions = [...formData.pricingOptions];
    updatedOptions[index][field] = value;
    setFormData({ ...formData, pricingOptions: updatedOptions });
  };

  const addPricingOption = () => {
    setFormData({
      ...formData,
      pricingOptions: [...formData.pricingOptions, { durationText: "", durationValue: "", price: "", subtitle: "" }],
    });
  };

  const removePricingOption = (index) => {
    const updatedOptions = formData.pricingOptions.filter((_, i) => i !== index);
    setFormData({ ...formData, pricingOptions: updatedOptions });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      let res;
      let payload = {};

      if (activeTab === "gallery") {
        if (!formData.days || !formData.charges) {
          toast.error("All fields are required");
          setLoading(false);
          return;
        }
        if (Number(formData.days) < 0 || Number(formData.charges) < 0) {
          toast.error("Negative numbers are not allowed");
          setLoading(false);
          return;
        }
        payload = {
          days: Number(formData.days),
          charges: Number(formData.charges),
        };
        res = await createGalleryPlanAPI(payload);
      } else if (activeTab === "hourly") {
        if (!formData.role) {
          toast.error("Role is required");
          setLoading(false);
          return;
        }

        if (formData.pricingType === "fixed") {
          if (!formData.fixedPrice) {
            toast.error("Fixed price is required");
            setLoading(false);
            return;
          }
          if (Number(formData.fixedPrice) < 0) {
            toast.error("Price cannot be negative");
            setLoading(false);
            return;
          }
        }

        if (formData.pricingType === "duration_based") {
          if (formData.pricingOptions.length === 0) {
            toast.error("At least one pricing option is required");
            setLoading(false);
            return;
          }
          for (let opt of formData.pricingOptions) {
            if (!opt.durationText || !opt.durationValue || !opt.price) {
              toast.error("All pricing fields (Duration, Value, Price) are required");
              setLoading(false);
              return;
            }
            if (Number(opt.durationValue) < 0 || Number(opt.price) < 0) {
              toast.error("Duration and Price cannot be negative");
              setLoading(false);
              return;
            }
          }
        }

        payload = {
          category: subTab,
          teamCategory: subTab,
          role: formData.role,
          pricingType: formData.pricingType,
          features: formData.features.filter(f => f.trim() !== ""),
          pricingOptions: formData.pricingType === "duration_based" ? formData.pricingOptions.map(opt => ({
            durationText: opt.durationText,
            durationValue: Number(opt.durationValue),
            price: Number(opt.price),
            subtitle: opt.subtitle
          })) : [],
          fixedPrice: formData.pricingType === "fixed" ? Number(formData.fixedPrice) : undefined
        };
        res = await createPhotographyPlanAPI(payload);
      } else if (activeTab === "editing") {
        if (!formData.price || !formData.numberOfVideos) {
          toast.error("Videos and Price are required");
          setLoading(false);
          return;
        }
        if (Number(formData.price) < 0) {
          toast.error("Price cannot be negative");
          setLoading(false);
          return;
        }
        payload = {
          numberOfVideos: formData.numberOfVideos,
          price: Number(formData.price),
          subtitle: formData.subtitle,
          planCategory: formData.planCategory,
          features: formData.features.filter(f => f.trim() !== "")
        };
        res = await createEditingPlanAPI(payload);
      }

      if (res?.data?.success) {
        refreshData();
        onClose();
        toast.success(res?.data?.message || "Plan added successfully");
        setFormData({
          days: "",
          charges: "",
          role: "",
          pricingType: "duration_based",
          pricingOptions: [{ durationText: "", durationValue: "", price: "", subtitle: "" }],
          fixedPrice: "",
          features: [""],
          numberOfVideos: "",
          price: "",
          subtitle: "",
          planCategory: "standard"
        });
      } else {
        toast.error(res?.data?.message || "Failed to add plan");
      }
    } catch (error) {
      console.error("Add plan failed:", error);
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <Modal show={show} onHide={onClose} centered size="md">
      <div className="custom-modal">
        <h4>Add {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Plan ({subTab})</h4>

        <div className="modal-content-scroll">
          {activeTab === "gallery" && (
            <div className="row">
              <div className="col-md-6 form-group">
                <label>Plan Duration (Days) <span className="text-danger">*</span></label>
                <input type="number" name="days" value={formData.days} onChange={handleChange} onKeyDown={preventInvalidChars} min="0" className="custom-input compact" placeholder="e.g. 30" />
              </div>
              <div className="col-md-6 form-group">
                <label>Plan Charges (₹) <span className="text-danger">*</span></label>
                <input type="number" name="charges" value={formData.charges} onChange={handleChange} onKeyDown={preventInvalidChars} min="0" className="custom-input compact" placeholder="e.g. 500" />
              </div>
            </div>
          )}

          {activeTab === "hourly" && (
            <div className="hourly-form-wrapper">
              <div className="modal-section">
                <div className="section-title">General Details</div>
                <div className="row">
                  <div className="col-md-6 form-group">
                    <label>Role <span className="text-danger">*</span></label>
                    <select name="role" value={formData.role} onChange={handleChange} className="custom-input compact">
                      <option value="">Select Role</option>
                      <option value="Photographer">Photographer</option>
                      <option value="Videographer">Videographer</option>
                      <option value="Lighting Setup">Lighting Setup</option>
                      <option value="Editing">Editing</option>
                    </select>
                  </div>
                  <div className="col-md-6 form-group">
                    <label>Pricing Type <span className="text-danger">*</span></label>
                    <input type="text" value="Duration Based" className="custom-input compact" disabled />
                  </div>
                </div>
              </div>

              {formData.pricingType === "fixed" && (
                <div className="modal-section">
                  <div className="section-title">Fixed Pricing</div>
                  <div className="row">
                    <div className="col-md-6 form-group">
                      <label>Fixed Price (₹) <span className="text-danger">*</span></label>
                      <input type="number" name="fixedPrice" value={formData.fixedPrice} onChange={handleChange} onKeyDown={preventInvalidChars} min="0" className="custom-input compact" placeholder="e.g. 5000" />
                    </div>
                  </div>
                </div>
              )}

              {formData.pricingType === "duration_based" && (
                <div className="modal-section">
                  <div className="section-title">Pricing Options <span className="text-danger">*</span></div>
                  <div className="pricing-options-list">
                    {formData.pricingOptions.map((opt, index) => (
                      <div key={index} className="pricing-option-row" style={{borderBottom: "1px solid #eee", paddingBottom: "15px"}}>
                        <div className="row">
                          <div className="col-md-4">
                            <label>Duration Text <span className="text-danger">*</span></label>
                            <input type="text" placeholder="e.g. 2 Hours" value={opt.durationText} onChange={(e) => handlePricingOptionChange(index, "durationText", e.target.value)} className="custom-input compact" />
                          </div>
                          <div className="col-md-4">
                            <label>Value (Hrs) <span className="text-danger">*</span></label>
                            <input type="number" placeholder="e.g. 2" value={opt.durationValue} onChange={(e) => handlePricingOptionChange(index, "durationValue", e.target.value)} onKeyDown={preventInvalidChars} min="0" className="custom-input compact" />
                          </div>
                          <div className="col-md-4">
                            <label>Price (₹) <span className="text-danger">*</span></label>
                            <input type="number" placeholder="e.g. 2000" value={opt.price} onChange={(e) => handlePricingOptionChange(index, "price", e.target.value)} onKeyDown={preventInvalidChars} min="0" className="custom-input compact" />
                          </div>
                          <div className="col-md-10">
                            <label>Subtitle (Optional)</label>
                            <input type="text" placeholder="e.g. Best for small events" value={opt.subtitle} onChange={(e) => handlePricingOptionChange(index, "subtitle", e.target.value)} className="custom-input compact" />
                          </div>
                          <div className="col-md-2 d-flex align-items-end">
                            <button className="btn-remove" style={{ marginBottom: "5px" }} onClick={() => removePricingOption(index)}>×</button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <button className="btn-add-option" onClick={addPricingOption}>+ Add Another Duration</button>
                  </div>
                </div>
              )}

              <div className="modal-section">
                <div className="section-title">Features</div>
                <div className="features-list">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <input type="text" placeholder="e.g. High resolution photos" value={feature} onChange={(e) => handleFeatureChange(index, e.target.value)} className="custom-input compact mb-0" />
                      <button className="btn-remove" onClick={() => removeFeature(index)}>×</button>
                    </div>
                  ))}
                  <button className="btn-add-option mt-2" onClick={addFeature}>+ Add Feature</button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "editing" && (
            <div className="row">
              <div className="col-md-6 form-group">
                <label>Number of Videos <span className="text-danger">*</span></label>
                <input type="text" name="numberOfVideos" value={formData.numberOfVideos} onChange={handleChange} className="custom-input compact" placeholder="e.g. 1-2 Videos" />
              </div>
              <div className="col-md-6 form-group">
                <label>Price (₹) <span className="text-danger">*</span></label>
                <input type="number" name="price" value={formData.price} onChange={handleChange} onKeyDown={preventInvalidChars} min="0" className="custom-input compact" placeholder="e.g. 1500" />
              </div>
              <div className="col-md-6 form-group">
                <label>Subtitle</label>
                <input type="text" name="subtitle" value={formData.subtitle} onChange={handleChange} className="custom-input compact" placeholder="e.g. Color Grading & Transition" />
              </div>
              <div className="col-md-6 form-group">
                <label>Plan Category <span className="text-danger">*</span></label>
                <select name="planCategory" value={formData.planCategory} onChange={handleChange} className="custom-input compact">
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div className="col-md-12">
                <div className="modal-section mt-3">
                  <div className="section-title">Features</div>
                  <div className="features-list">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="feature-item">
                        <input type="text" placeholder="e.g. Basic color grading" value={feature} onChange={(e) => handleFeatureChange(index, e.target.value)} className="custom-input compact mb-0" />
                        <button className="btn-remove" onClick={() => removeFeature(index)}>×</button>
                      </div>
                    ))}
                    <button className="btn-add-option mt-2" onClick={addFeature}>+ Add Feature</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" onClick={handleSubmit}>Create Plan</button>
        </div>
      </div>
    </Modal>
  );
};

export default AddGalleryPlanModal;